import { useMemo } from "react";

import {
  type LanguageCodes,
  PriceCategoryType,
  type ProjectActorVoiceListItemDto,
  type ProjectDetailDto,
} from "Infrastructure/Api/Api";
import {
  isMediaSegment,
  isPauseSegment,
  isVoiceSegment,
  type ScriptSegmentFE,
} from "Projects/Components/ScriptEditor/ScriptEditorContext";
import { flattenSpans } from "Projects/Utils/spanUtils";

const CHARS_PER_SECOND: Partial<Record<LanguageCodes, number>> = {
  Czech: 14,
  English: 15,
  Deutsch: 14,
};

const DEFAULT_CHARS_PER_SECOND = 14;

interface ScriptEstimationInput {
  segments: ScriptSegmentFE[];
  globalSpeedFactor: number;
  project: ProjectDetailDto | undefined;
  actorVoices: ProjectActorVoiceListItemDto[] | undefined;
  language: LanguageCodes | null | undefined;
}

interface ScriptEstimation {
  estimatedDurationSeconds: number;
  estimatedPrice: number | null;
}

export function useScriptEstimation({
  segments,
  globalSpeedFactor,
  project,
  actorVoices,
  language,
}: ScriptEstimationInput): ScriptEstimation {
  return useMemo(() => {
    const charsPerSec =
      (language && CHARS_PER_SECOND[language]) || DEFAULT_CHARS_PER_SECOND;

    // Voice + pause segments play sequentially and define the timeline length.
    // Media segments play in parallel (background audio) starting at
    // `startAtMs` on the timeline — they only extend total duration if they
    // finish after the sequential tail.
    let sequentialSeconds = 0;
    let mediaEndSeconds = 0;

    for (const seg of segments) {
      if (isVoiceSegment(seg)) {
        const text = flattenSpans(seg.spans);
        const charCount = text.length;
        const effectiveSpeed =
          (seg.speedFactor || 1) * (globalSpeedFactor || 1);
        sequentialSeconds += charCount / charsPerSec / effectiveSpeed;
      } else if (isPauseSegment(seg)) {
        sequentialSeconds += seg.durationMs / 1000;
      } else if (isMediaSegment(seg)) {
        const startSec = (seg.startAtMs ?? 0) / 1000;
        const durSec = seg.durationMs ? seg.durationMs / 1000 : 0;
        mediaEndSeconds = Math.max(mediaEndSeconds, startSec + durSec);
      }
    }

    const totalSeconds = Math.max(sequentialSeconds, mediaEndSeconds);

    if (!project || project.hasExclusiveContracts) {
      return { estimatedDurationSeconds: totalSeconds, estimatedPrice: null };
    }

    // Map actorVoiceID → actorID
    const voiceMap = new Map(
      (actorVoices ?? []).map((v) => [v.actorVoiceID, v.actorID]),
    );

    // Collect unique actorIDs used in voice segments
    const actorIDs = new Set<string>();
    for (const seg of segments) {
      if (isVoiceSegment(seg)) {
        const actorID = voiceMap.get(seg.actorVoiceID);
        if (actorID) actorIDs.add(actorID);
      }
    }

    const actorPriceMap = new Map(project.actors.map((a) => [a.actorID, a]));

    let totalPrice = 0;
    const durationMinutes = totalSeconds / 60;

    for (const actorID of actorIDs) {
      const actor = actorPriceMap.get(actorID);
      if (!actor || actor.isExclusiveContract) continue;

      const actorPrice =
        project.priceCategoryType === PriceCategoryType.PerMinute
          ? actor.estimatedPrice * durationMinutes
          : actor.estimatedPrice;

      totalPrice += actorPrice;
    }

    return {
      estimatedDurationSeconds: totalSeconds,
      estimatedPrice: totalPrice,
    };
  }, [segments, globalSpeedFactor, project, actorVoices, language]);
}
